import clsx from 'clsx';
import { useRef, useState, useLayoutEffect } from '@wordpress/element';
import {
	__experimentalConfirmDialog as ConfirmDialog,
	Button,
} from '@wordpress/components';
// eslint-disable-next-line @wordpress/use-recommended-components -- Intentional early adoption of the new Menu, pending WordPress/gutenberg#76135.
import { Button as UIButton, Menu } from '@wordpress/ui';
import { __, _x, sprintf } from '@wordpress/i18n';
import { moreVertical, published } from '@wordpress/icons';
import { NoteCard } from './note-card';
import { NoteForm } from './note-form';
import { NoteReactions } from './reactions/note-reactions';

function NoteActionsMenu( { items, buttonRef } ) {
	return (
		<Menu.Root
			// Let outside interactions reach the note thread's focus-out
			// handling so it can clear the selection.
			modal={ false }
		>
			<Menu.Trigger
				disabled={ ! items.length }
				render={
					<Button
						ref={ buttonRef }
						size="small"
						icon={ moreVertical }
						label={ __( 'Actions' ) }
						accessibleWhenDisabled
					/>
				}
			/>
			<Menu.Popup
				positioner={ <Menu.Positioner side="bottom" align="end" /> }
			>
				{ items.map( ( item ) => (
					<Menu.Item key={ item.id } onClick={ item.onClick }>
						<Menu.ItemLabel>{ item.title }</Menu.ItemLabel>
					</Menu.Item>
				) ) }
			</Menu.Popup>
		</Menu.Root>
	);
}

export function Note( {
	note,
	parentNote,
	isSelected,
	onEditNote,
	onDeleteNote,
	onResolve,
	isThreadResolved = false,
} ) {
	const [ actionState, setActionState ] = useState( null );
	const actionButtonRef = useRef( null );

	const commentRef = useRef( null );
	const rawContent = note?.content?.raw;
	const [ prevContent, setPrevContent ] = useState( rawContent );
	const [ isExpanded, setIsExpanded ] = useState( false );
	const [ isOverflowing, setIsOverflowing ] = useState( false );

	// Collapse whenever the content changes so it can be re-measured.
	if ( prevContent !== rawContent ) {
		setPrevContent( rawContent );
		setIsExpanded( false );
	}

	// Measure the (clamped) content to decide whether the toggle is needed.
	useLayoutEffect( () => {
		const commentElement = commentRef.current;
		if ( commentElement ) {
			setIsOverflowing(
				commentElement.scrollHeight > commentElement.clientHeight
			);
		}
	}, [ rawContent ] );

	const canResolve = note.parent === 0;
	const metaStatus = note.meta?._wp_note_status;
	const isResolutionNote =
		metaStatus === 'resolved' || metaStatus === 'reopen';
	const hasUserText =
		typeof rawContent === 'string' && rawContent.trim() !== '';

	const hasResolved = ( status ) =>
		status === 'approved' || parentNote?.status === 'approved';

	const menuItems = [
		{
			id: 'edit',
			title: __( 'Edit' ),
			isEligible: ( { status } ) =>
				( ! isResolutionNote || hasUserText ) &&
				! hasResolved( status ),
			onClick: () => setActionState( 'edit' ),
		},
		{
			id: 'reopen',
			title: _x( 'Reopen', 'Reopen note' ),
			isEligible: ( { status } ) => canResolve && hasResolved( status ),
			onClick: () => onEditNote( note, { status: 'hold' } ),
		},
		{
			id: 'delete',
			title: __( 'Delete' ),
			isEligible: ( { status } ) =>
				canResolve || ( ! isResolutionNote && ! hasResolved( status ) ),
			onClick: () => setActionState( 'delete' ),
		},
	];
	const availableItems = menuItems.filter( ( item ) =>
		item.isEligible( note )
	);

	const deleteConfirmMessage =
		note.parent === 0
			? __(
					"Are you sure you want to delete this note? This will also delete all of this note's replies."
				)
			: __( 'Are you sure you want to delete this reply?' );

	const handleCancel = () => {
		setActionState( null );
		actionButtonRef.current?.focus();
	};

	let body;
	if ( actionState === 'edit' ) {
		body = (
			<NoteForm
				onSubmit={ async ( value ) => {
					const saved = await onEditNote( note, {
						content: value,
					} );
					// Keep the form open on failure so the edit isn't lost.
					if ( saved ) {
						handleCancel();
					}
					return saved;
				} }
				onCancel={ handleCancel }
				note={ note }
				labels={ {
					submit: _x( 'Update', 'verb' ),
					input: sprintf(
						// translators: %1$s: note identifier, %2$s: author name.
						__( 'Edit note %1$s by %2$s' ),
						note.id,
						note.author_name
					),
				} }
			/>
		);
	} else {
		let content;
		if ( isResolutionNote ) {
			const actionText =
				note.meta._wp_note_status === 'resolved'
					? __( 'Marked as resolved' )
					: __( 'Reopened' );
			const raw = note?.content?.raw;
			content =
				raw && typeof raw === 'string' && raw.trim() !== ''
					? sprintf(
							// translators: %1$s: action label ("Marked as resolved" or "Reopened"); %2$s: note text.
							__( '%1$s: %2$s' ),
							actionText,
							raw
						)
					: actionText;
		} else {
			content = note?.content?.rendered;
		}

		body = (
			<div
				ref={ commentRef }
				className={ clsx( 'editor-collab-sidebar-panel__note-content', {
					'editor-collab-sidebar-panel__resolution-text':
						isResolutionNote,
					'is-collapsed': ! isExpanded,
				} ) }
				dangerouslySetInnerHTML={ { __html: content ?? '' } }
			/>
		);
	}

	const actions = isSelected ? (
		<>
			{ canResolve && onResolve && (
				<Button
					label={ _x( 'Resolve', 'Mark note as resolved' ) }
					size="small"
					icon={ published }
					disabled={ note.status === 'approved' }
					accessibleWhenDisabled={ note.status === 'approved' }
					onClick={ onResolve }
				/>
			) }
			<NoteActionsMenu
				items={ availableItems }
				buttonRef={ actionButtonRef }
			/>
		</>
	) : null;

	return (
		<NoteCard
			note={ note }
			actions={ actions }
			className="editor-collab-sidebar-panel__note"
			role={ note.parent !== 0 ? 'treeitem' : undefined }
		>
			<div className="editor-collab-sidebar-panel__note-body">
				{ body }
				<NoteReactions
					noteId={ note.id }
					// Not while editing: the trigger floats over the form's
					// text field.
					canReact={
						isSelected &&
						actionState !== 'edit' &&
						! isThreadResolved
					}
					disabled={ isThreadResolved }
				/>
			</div>
			{ actionState === 'delete' && (
				<ConfirmDialog
					isOpen
					onConfirm={ () => {
						onDeleteNote( note );
						setActionState( null );
					} }
					onCancel={ handleCancel }
					confirmButtonText={ __( 'Delete' ) }
				>
					{ deleteConfirmMessage }
				</ConfirmDialog>
			) }
			{ isOverflowing && 'edit' !== actionState && (
				<UIButton
					className="editor-collab-sidebar-panel__show-more-button"
					variant="unstyled"
					size="small"
					onClick={ () => setIsExpanded( ! isExpanded ) }
				>
					{ ! isExpanded ? __( 'Show more' ) : __( 'Show less' ) }
				</UIButton>
			) }
		</NoteCard>
	);
}
